import { MAX_SUPPORT_SUBJECT_CHARS } from '@zuruflex/core';
import type { MessageThreadDetail } from '@zuruflex/types';
import {
  Button,
  describeError,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  TagInput,
} from '@zuruflex/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Subject and tags, behind a dialog rather than inline in the context pane.
 *
 * Both are typed into, and the pane they would sit in is fed by
 * `useThread` — which listens to the thread's own event stream and
 * rewrites what is on screen the instant a colleague or the customer says
 * anything. An uncontrolled input in that pane loses half a word to an
 * incoming message; a controlled one either fights the refresh or ignores
 * it. A dialog holds its own draft for the few seconds it is open and
 * writes once, which is the only shape that does not lose keystrokes.
 *
 * The status, priority, topic and assignee controls stay in the pane
 * because none of them is typed into: a `Select` that re-renders under a
 * fresh value is a `Select` showing the truth.
 */
export interface EditThreadDialogProps {
  thread: MessageThreadDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (patch: { subject: string; tags: string[] }) => Promise<unknown>;
  pending?: boolean;
  error?: unknown;
}

export function EditThreadDialog({
  thread,
  open,
  onOpenChange,
  onSave,
  pending,
  error,
}: EditThreadDialogProps) {
  const { t } = useTranslation(['admin', 'common']);

  /*
   * Keyed on the thread and on the open flag, so the draft is seeded from
   * whatever is current each time it is opened rather than from whatever
   * the thread said when the pane first mounted.
   */
  const [draft, setDraft] = useState({ subject: '', tags: [] as string[] });
  const [seeded, setSeeded] = useState('');
  const seed = `${thread.id}:${open}`;
  if (seed !== seeded) {
    setSeeded(seed);
    setDraft({ subject: thread.subject ?? '', tags: thread.tags });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('inbox.handling')}</DialogTitle>
          <DialogDescription>{thread.reference}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label={t('common:support.subject')}>
            {(field) => (
              <Input
                {...field}
                value={draft.subject}
                maxLength={MAX_SUPPORT_SUBJECT_CHARS}
                placeholder={t('common:support.subjectPlaceholder')}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, subject: event.target.value }))
                }
              />
            )}
          </FormField>

          <FormField label={t('inbox.tags')}>
            {(field) => (
              <TagInput
                {...field}
                value={draft.tags}
                onChange={(tags) => setDraft((current) => ({ ...current, tags }))}
                labels={{
                  placeholder: t('inbox.tagsPlaceholder'),
                  // No `action.remove` in the catalogue; this is the one
                  // key that already says "Remove {{name}}" in all three.
                  remove: (tag) => t('common:messaging.removeAttachment', { name: tag }),
                }}
              />
            )}
          </FormField>

          {/* A control that silently does nothing is worse than an error. */}
          {error ? <p className="text-xs text-danger">{describeError(error)}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            isLoading={pending}
            disabled={pending}
            onClick={() => {
              // Closed only on success — the dialog is where the error
              // has to be read, and one that shuts on a failed save takes
              // the explanation and the draft with it.
              void onSave(draft)
                .then(() => onOpenChange(false))
                .catch(() => undefined);
            }}
          >
            {t('common:action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
